import { CloudOff, RefreshCw, SearchX, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { EmptyState } from './EmptyState';
import { LoadingOverlay } from './LoadingOverlay';
import { isNotFoundError } from '../../utils/errors';
import { useIsOfflineOptional } from '../../offline/OfflineStatusContext';

interface EntityLoadStateProps {
  /**
   * Entity key into the `common:entities.*` registry (e.g. `"deal"`, `"bills"`).
   * Resolved through i18n so terminology overrides apply ({{Deal}} etc.).
   * Use the SINGULAR key for detail pages and the PLURAL key for list pages,
   * matched to {@link variant}. Omit it for content that isn't a single named
   * entity (settings panels, reports, integrations) to get generic copy.
   */
  entityType?: string;
  /**
   * The fetched data. When present (not `undefined`/`null`) this component
   * renders nothing — the caller should render the real content instead. This
   * is what makes the offline-with-cache case "just work": cached data is
   * defined, so even though `error` is set from the failed background refetch,
   * no error state is shown and the page renders the cached entity.
   */
  data?: unknown;
  /** The query error, if any. Only consulted when there is no `data` to show. */
  error?: unknown;
  /** React Query `isLoading`. Shows a spinner when there is nothing cached yet. */
  isLoading?: boolean;
  /**
   * Retry handler — usually a React Query `refetch`. A retry button is shown
   * when provided. It is disabled while offline (nothing to retry until the
   * connection is back) and enabled for online load failures.
   */
  onRetry?: () => void;
  /**
   * Copy shape. `'detail'` → "This {{entity}} isn't available offline."
   * `'list'` → "{{entity}} aren't available offline." Defaults to `'detail'`.
   */
  variant?: 'detail' | 'list';
  className?: string;
}

/**
 * The single, system-wide offline-aware load state for every detail and list
 * page. It resolves one of four states from the standard React Query trio
 * (`data` / `error` / `isLoading`) plus the shared connectivity signal
 * ({@link useIsOffline} → `OfflineStatusContext`, Capacitor-backed on native):
 *
 * Connectivity is read via {@link useIsOfflineOptional} (a non-throwing variant
 * of `useIsOffline`) so this component degrades to "online" rather than crashing
 * a page that renders it outside an `OfflineStatusProvider` (tests, storybook).
 *
 * 1. **Data present** (online success, or offline-with-cache) → renders nothing;
 *    the caller renders the real content. Cached data is never hidden behind an
 *    error banner.
 * 2. **Loading, nothing cached** → spinner.
 * 3. **No data + offline** → "You're offline / This {{entity}} isn't available
 *    offline…" empty state (signal-loss icon, retry disabled).
 * 4. **No data + online 404** → "{{entity}} not found / doesn't exist or isn't
 *    shared with you" — the Contract-D 404-hide deny path. No retry (it can
 *    never succeed), no alarming "something went wrong" copy.
 * 5. **No data + other online failure (5xx etc.)** → "Failed to load {{entity}}"
 *    with an enabled retry. No connectivity hint.
 *
 * Render it in the page's "nothing to show" branch, wrapped in the page's own
 * `PageContainer`:
 *
 * ```tsx
 * if (!deal) {
 *   return (
 *     <PageContainer>
 *       <EntityLoadState entityType="deal" data={deal} error={error} isLoading={isLoading} onRetry={refetch} />
 *     </PageContainer>
 *   );
 * }
 * ```
 *
 * Read-query fallbacks only. Mutation/upload errors, toasts, and
 * auth/session-expired messages must NOT use this — they need explicit feedback
 * regardless of connectivity.
 */
export function EntityLoadState({
  entityType,
  data,
  error,
  isLoading = false,
  onRetry,
  variant = 'detail',
  className,
}: EntityLoadStateProps) {
  const { t } = useTranslation('common');
  const offline = useIsOfflineOptional();

  // Data is available (fresh or cached) — let the caller render it.
  if (data !== undefined && data !== null) {
    return null;
  }

  // Nothing cached yet and a fetch is in flight — show a spinner, not an error.
  if (isLoading && !error) {
    return <LoadingOverlay className={className} />;
  }

  const entity = entityType ? t(`entities.${entityType}`) : undefined;

  if (offline) {
    return (
      <EmptyState
        icon={CloudOff}
        title={t('offline.entityUnavailableTitle')}
        message={
          entity
            ? t(
                variant === 'list'
                  ? 'offline.entityUnavailableList'
                  : 'offline.entityUnavailableDetail',
                { entity }
              )
            : t('offline.genericUnavailable')
        }
        className={className}
      >
        {onRetry && (
          <Button variant="outline" size="sm" disabled className="mt-1">
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('app.retry')}
          </Button>
        )}
      </EmptyState>
    );
  }

  // Deny path: a 404 is the Contract-D "404-hide" — indistinguishable from
  // "doesn't exist". Never surface it as a system error with a dead retry;
  // show a calm not-found / no-access state instead. (A genuine 5xx falls
  // through to the retryable failure state below.)
  if (isNotFoundError(error)) {
    return (
      <EmptyState
        icon={SearchX}
        title={entity ? t('offline.entityNotFoundTitle', { entity }) : t('offline.genericNotFoundTitle')}
        message={
          entity ? t('offline.entityNotFoundMessage', { entity }) : t('offline.genericNotFoundMessage')
        }
        className={className}
      />
    );
  }

  return (
    <EmptyState
      icon={TriangleAlert}
      title={entity ? t('offline.entityLoadFailedTitle', { entity }) : t('offline.genericLoadFailedTitle')}
      message={t('offline.entityLoadFailedMessage')}
      className={className}
    >
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-1 text-foreground">
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('app.retry')}
        </Button>
      )}
    </EmptyState>
  );
}
